import { GetCallResponse } from '@distribute/shared/api-types/call';
import { Button, Dropdown } from '../../../../../shared/ui';
import { useTiptapEditor } from '../../../../../entities/tiptap-editor';
import { Dispatch, FC, SetStateAction } from 'react';
import { callModel } from '../../../../../features/call';
import { useDispatch, useSelector } from 'react-redux';
import { JSONContent } from '@tiptap/core';
import { JSONContentBuilder } from '@distribute/shared/tiptap/json-content';
import { DEFAULT_RATIO } from '@distribute/shared/utils';
import { InsertVariant } from './lib/config';
import { IconMap } from '../../../../../shared/sprite';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useSubscriptionLimits } from '../../../../../features/subscription/hooks';
import { cn } from '@distribute/frontend/utils';

import { editorLeftSidebarModel } from '../../../model';

type Props = {
  call: GetCallResponse;
  setInsertVariant: Dispatch<SetStateAction<InsertVariant | null>>;
  setMakePublicConfirmationCallId: Dispatch<SetStateAction<string | null>>;
  isPreview?: boolean;
};

export const InsertCallButton: FC<Props> = ({
  call,
  setInsertVariant,
  setMakePublicConfirmationCallId,
  isPreview = false,
}) => {
  const { editor } = useTiptapEditor();
  const dispatch = useDispatch();

  const { isCallSummaryEnabled } = useSubscriptionLimits();

  const isLoading = useSelector(callModel.selectors.selectIsInsertCallLoading);

  const handleInsertCallWithSummary = () => {
    if (call.muxAsset.playback.policy === 'signed') {
      setInsertVariant(InsertVariant.WITH_SUMMARY);
      setMakePublicConfirmationCallId(call.id);
      return;
    }

    dispatch(
      callModel.actions.loadCallDetailsForEditor({
        id: call.id,
        isInsertSummary: true,
        cb: (content: JSONContent[]) => {
          editor
            ?.chain()
            .insertContent([...content])
            .focus()
            .run();
        },
      })
    );
    dispatch(editorLeftSidebarModel.actions.setSelectedOption(null));
  };

  const handleInsertCallVideoOnly = () => {
    if (call.muxAsset.playback.policy === 'signed') {
      setInsertVariant(InsertVariant.ONLY_VIDEO);
      setMakePublicConfirmationCallId(call.id);
      return;
    }

    const content = JSONContentBuilder.videoRecord({
      attrs: {
        playbackId: call.muxAsset.playback.id,
        aspectRatio: call.muxAsset.data.aspectRatio || DEFAULT_RATIO,
        prefix: call.id,
      },
    }).asArray();

    editor
      ?.chain()
      .insertContent([...content])
      .focus()
      .run();

    dispatch(editorLeftSidebarModel.actions.setSelectedOption(null));
  };

  const insertOptions = [
    {
      label: 'Insert Summary & Video',
      id: 'summary',
      iconName: IconMap.VideoAndSummary,
      iconWidth: 16,
      onClick: handleInsertCallWithSummary,
      hidden: !isCallSummaryEnabled,
    },
    {
      label: 'Insert video only to page',
      id: 'video',
      iconName: IconMap.FilmArrow,
      iconWidth: 16,
      style: 'sm:hidden',
      onClick: handleInsertCallVideoOnly,
    },
  ].filter((control) => !control.hidden);

  return (
    <Dropdown
      align={isPreview ? 'end' : 'center'}
      items={insertOptions}
      listStyles="shadow-lg"
      itemStyles="hover:text-gray-800"
      isModalMode={false}
      triggerComponent={
        <DropdownMenu.Trigger>
          <Button
            variant="text"
            color="primary"
            className={cn({ 'w-45 !h-11 text-md': !isPreview })}
            loading={isLoading}
          >
            Insert
          </Button>
        </DropdownMenu.Trigger>
      }
    />
  );
};
